﻿import {useMemo} from "react";
import {useGeocodeByAddress} from "../maps/useGeocodeByAddress";
import {useGeocodeByLatLng} from "../maps/useGeocodeByLatLng";
import {LocationSearchParams} from "./useLocationSearchParams";
import {getQueryParamsFromPlace} from "../../util/AddressUtils";
import _ from "underscore";

type GeocoderResult = google.maps.GeocoderResult;

interface IGeocodedPlaceByParamsParams {
  currentPlace: GeocoderResult | undefined,
  currentLocationParams: LocationSearchParams | undefined,
  /**
   * When true, resolve nothing from the URL params: no forward/reverse geocode,
   * and report "not failed" so the fallback/redirect cascade stays dormant.
   * Used in coordinate mode (after a map drag) — the search is purely coordinate
   * driven, so we deliberately don't resolve a place/label for the search box.
   */
  skip?: boolean
}

/**
 * Hook to get a geocoded place based on URL location params, unless the currentPlace already matches those params.
 *
 * Resolution strategy (in priority order):
 * 1. If currentPlace already matches URL params → return currentPlace (no API call).
 * 2. If city & state are present in URL params → forward-geocode the address text.
 *    This keeps the result at the city/state (locality) level even when the URL
 *    also carries lat/lng (e.g. the "Popular Locations" links), so we never
 *    fabricate a precise street address from reverse-geocoding the coordinates.
 *    A street number/name in the params still narrows the forward-geocode result.
 * 3. If lat & lng are present (and no city/state) → reverse-geocode by coordinates.
 *    This covers "lat+lng only" URLs from browser/IP geolocation.
 * 4. Otherwise → mark as failed so the cascade can try fallback sources.
 *
 * @param currentPlace the currently selected GeocoderResult place
 * @param currentLocationParams the location params from the URL
 * @param skip
 */
export const useGeocodedPlaceByParams = ({currentPlace, currentLocationParams, skip = false}: IGeocodedPlaceByParamsParams) => {
  // Check if currentPlace already matches the URL params (ignoring lat/lng for comparison
  // since floating-point string representations can drift between geocoder calls)
  const currentPlaceMatchesParams = useMemo(() => {
    if (!currentPlace || !currentLocationParams) return false;
    // Exclude street-level fields from comparison — the search URL only carries
    // locality-level params (city, state, country) so a reverse-geocoded place
    // that happens to include street info should still count as a match.
    const placeParams = getQueryParamsFromPlace(currentPlace, 'short_name', false);
    if (!placeParams) return false;
    // Compare the locality-level fields only (not lat/lng) to determine if we already have the right place
    const {lat: _pLat, lng: _pLng, ...placeAddress} = placeParams;
    const {lat: _cLat, lng: _cLng, street_number: _cSn, street_name: _cSt, ...paramAddress} = currentLocationParams;
    return _.isEqual(placeAddress, paramAddress);
  }, [currentPlace, currentLocationParams]);

  // Determine if URL provides usable lat/lng (must be finite numbers, not NaN/Infinity)
  const parsedLat = currentLocationParams?.lat ? parseFloat(currentLocationParams.lat) : NaN;
  const parsedLng = currentLocationParams?.lng ? parseFloat(currentLocationParams.lng) : NaN;
  const hasLatLng = Number.isFinite(parsedLat) && Number.isFinite(parsedLng);

  // Whether the URL carries address text (city + state). When present we always
  // forward-geocode the address so the result stays at the city/state level,
  // even if lat/lng are also in the URL (e.g. "Popular Locations" links).
  const hasCityState = !!(currentLocationParams?.city && currentLocationParams?.state);

  // --- Path A: reverse-geocode by lat/lng (only for pure lat/lng URLs, no address text) ---
  const reverseGeocodeLat = useMemo(() => {
    if (skip || currentPlaceMatchesParams || hasCityState || !hasLatLng) return undefined;
    return parsedLat;
  }, [skip, currentPlaceMatchesParams, hasCityState, hasLatLng, parsedLat]);

  const reverseGeocodeLng = useMemo(() => {
    if (skip || currentPlaceMatchesParams || hasCityState || !hasLatLng) return undefined;
    return parsedLng;
  }, [skip, currentPlaceMatchesParams, hasCityState, hasLatLng, parsedLng]);

  const {geocodedPlace: reverseGeocodedPlace, geocodingError: reverseGeocodingError} = useGeocodeByLatLng({
    latitude: reverseGeocodeLat,
    longitude: reverseGeocodeLng
  });

  // --- Path B: forward-geocode by address text (used whenever city & state are present) ---
  const address = useMemo(() => {
    // Skip if currentPlace already matches, or if there's no city/state to geocode
    // (in which case Path A will reverse-geocode any lat/lng instead).
    if (skip || currentPlaceMatchesParams || !hasCityState) return undefined;

    const currentStreetAddress = currentLocationParams?.street_number && currentLocationParams?.street_name ? `${currentLocationParams.street_number} ${currentLocationParams.street_name}, ` : '';
    const country = currentLocationParams?.country ? `, ${currentLocationParams.country}` : '';
    return `${currentStreetAddress}${currentLocationParams?.city}, ${currentLocationParams?.state}${country}`;
  }, [skip, currentLocationParams, currentPlaceMatchesParams, hasCityState]);

  // WI00991893: Bias towards US region if no country is provided
  const geocoderRequest = currentLocationParams?.country ? {address} : {address, region: 'US'};
  const {geocodingFailed: addressGeocodingFailed, geocodedAddress, geocodingLoading: addressGeocodingLoading} = useGeocodeByAddress(geocoderRequest);

  // --- Combine results ---
  // Path B (forward-geocode by address) takes priority whenever city & state exist.
  const usesAddressGeocode = hasCityState;

  const geocodingLoading = usesAddressGeocode
    ? addressGeocodingLoading
    : (hasLatLng && !reverseGeocodedPlace && !reverseGeocodingError && !currentPlaceMatchesParams);

  const geocodingFromParamsFailed = usesAddressGeocode
    ? ((!address && !addressGeocodingLoading && !currentPlaceMatchesParams) || !!addressGeocodingFailed)
    // A valid lat/lng URL is NEVER a "failure": the coordinates are a usable
    // location to center on, even if reverse-geocoding can't resolve a city
    // (e.g. rural areas). Reporting failure here would wrongly trigger the
    // fallback/redirect cascade and discard the user's explicit coordinates.
    : (hasLatLng ? false : !currentPlaceMatchesParams);

  let finalGeocodedAddress: GeocoderResult | undefined;
  if (currentPlaceMatchesParams) {
    finalGeocodedAddress = currentPlace;
  } else if (usesAddressGeocode) {
    finalGeocodedAddress = geocodedAddress;
  } else if (hasLatLng) {
    finalGeocodedAddress = reverseGeocodedPlace;
  }

  if (skip) {
    // Coordinate mode: resolve nothing, and report not-failed so the fallback
    // cascade (local storage / browser / IP / redirect) stays dormant.
    return {
      geocodedAddressFromParams: undefined,
      geocodedAddressFromParamsLoading: false,
      geocodingAddressFromParamsFailed: false
    }
  }

  return {
    geocodedAddressFromParams: finalGeocodedAddress,
    geocodedAddressFromParamsLoading: geocodingLoading && !currentPlaceMatchesParams,
    geocodingAddressFromParamsFailed: geocodingFromParamsFailed
  }
}
